<template>
  <Drop
    ref="breakBlockEl"
    :style="style"
    :accepts-type="['task', 'event', 'allocation']"
    :accepts-data="acceptsData"
    class="break-block"
    @dragleave="onDragLeave"
    @dragover="onDragOver"
    @drop="onDrop"
  >
    <div v-if="isEmpty && !isOvering" class="empty">
      <div class="cache" />
      <div class="empty-state">
        <p class="empty-state__text">No events today.</p>
        <img
          :src="emptyStateImage"
          class="empty-state__illu"
          alt=""
          aria-hidden="true"
        />
      </div>
    </div>

    <div v-else-if="isEmpty && isOvering" class="empty-dnd break">
      <Timeline :event="eventOrPause" class="timer" hide-time />
      <DraggingPlaceholder
        :drop-duration="dropDuration"
        :drop-time="dropTime"
        :placeholder-position="placeholderPosition"
        :calendar="selectedCalendar"
      />
    </div>

    <div v-else class="break">
      <Timeline
        :event="eventOrPause"
        class="progress-bar"
        is-break
        :hide-time="false"
      />
      <div class="break-space">
        <div
          v-if="!hideMessage"
          class="message"
          :class="{ evening: event.kind === 'evening' }"
        >
          <div
            v-if="ongoing && event.kind !== 'evening'"
            class="duration current"
            :class="{
              urgent: remainingTime.urgent,
            }"
          >
            {{ remainingTime.duration }} left
          </div>
          <div v-else-if="event.kind === 'pause'" class="duration">
            {{ formatDuration(duration) }}
          </div>
        </div>

        <DraggingPlaceholder
          :drop-duration="dropDuration"
          :drop-time="dropTime"
          :placeholder-position="placeholderPosition"
          :calendar="selectedCalendar"
          :show-calendar-info="showCalendarInfo"
          :is-duplicating-allocation="isDuplicatingAllocation"
        />
        <DraggingPlaceholder
          :drop-duration="dropDuration"
          :drop-time="dropTime"
          :placeholder-position="placeholderPosition"
          :calendar="selectedCalendar"
          :show-calendar-info="showCalendarInfo"
          :is-duplicating-allocation="isDuplicatingAllocation"
        />
      </div>
    </div>
  </Drop>
</template>

<script lang="ts">
import {
  add,
  formatDuration,
  intervalToDuration,
  isAfter,
  isBefore,
  isEqual,
} from "date-fns";
import {
  ComponentPublicInstance,
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  toRefs,
  watch,
} from "vue";
import { Drop } from "vue-easy-dnd";

import {
  Calendar,
  EventOrPause,
  EventPatch,
  Pause,
} from "@ctrl/controller-rpc";

import emptyStateNoEventsDark from "@/assets/illus/empty-state-no-events--dark.png";
import emptyStateNoEventsLight from "@/assets/illus/empty-state-no-events--light.png";
import {
  isDraggingAllocation,
  isDraggingTask,
  useDndEvent,
  useDropEvent,
} from "@/components/agenda/dnd";
import DraggingPlaceholder from "@/components/agenda/DraggingPlaceholder.vue";
import { useHeightOfDuration } from "@/components/agenda/pixels";
import { equalDates, formatDurationFull } from "@/components/dates";
import * as inject from "@/components/inject";
import { openUpgradeModal } from "@/components/modals/upgrade";
import { getDuration } from "@/event";
import { allocateTimeForTask } from "@/task";
import { DraggedAllocation, DraggedEvent, DraggedTask } from "@/types";

import { isCurrentEvent } from "./agenda";
import Timeline from "./Timeline.vue";

const DEFAULT_TASK_DURATION = 30;

const step = 10;
export default defineComponent({
  name: "BreakBlock",
  components: {
    DraggingPlaceholder,
    Drop,
    Timeline,
  },
  props: {
    date: {
      type: Date,
      required: true,
    },
    event: {
      type: Object as PropType<Pause>,
      required: true,
    },
    isEmpty: {
      type: Boolean,
      required: true,
    },
    resetDragSignal: {
      type: Number,
      required: false,
    },
    selectedCalendarId: {
      type: String,
      required: false,
    },
    calendarsMap: {
      type: Object as PropType<Record<string, Calendar>>,
      required: true,
    },
  },
  emits: {
    isOvering: (_isOvering: boolean) => true,
    scrollTop: (_scrollTop: boolean) => true,
    scrollBottom: (_scrollBottom: boolean) => true,
    setCurrentEventEl: (_currentEventEl: ComponentPublicInstance) => true,
    dragover: (_: DraggedTask | DraggedEvent | DraggedAllocation) => true,
    dragleave: () => true,
    drop: () => true,
    updateEvent: (_patch: EventPatch) => true,
  },
  setup(props, { emit }) {
    const controller = inject.controller();
    const profile = inject.profile();
    const billing = inject.billing();
    const now = inject.clock();
    const locale = inject.locale();
    const theme = inject.theme();
    const { date, isEmpty, event, selectedCalendarId, calendarsMap } =
      toRefs(props);

    const isEntitledToTimeBlocking = computed(
      () => billing.value?.isEntitledTo("time-blocking")?.value ?? false
    );

    const emptyStateImage = computed(() =>
      theme.value === "dark" ? emptyStateNoEventsDark : emptyStateNoEventsLight
    );
    const placeholderPosition = ref<number>();
    const isDuplicatingAllocation = ref<boolean>(false);
    const altPressed = ref<boolean>(false);
    const isOvering = ref<boolean>(false);
    const lastDraggedType = ref<"task" | "event" | "allocation" | null>(null);
    const breakBlockEl = ref<ComponentPublicInstance>();
    let altKeyDown: ((event: KeyboardEvent) => void) | undefined;
    let altKeyUp: ((event: KeyboardEvent) => void) | undefined;

    const selectedCalendar = computed(() => {
      return selectedCalendarId.value
        ? calendarsMap.value[selectedCalendarId.value]
        : undefined;
    });

    onMounted(() => {
      if (
        isCurrentEvent(event.value.start, event.value.end, now) &&
        breakBlockEl.value
      )
        emit("setCurrentEventEl", breakBlockEl.value);
      altKeyDown = (ev: KeyboardEvent) => {
        altPressed.value = !!ev.altKey;
        if (!isOvering.value || !draggingAllocation.value) return;
        if (altPressed.value) {
          isDuplicatingAllocation.value = true;
        }
      };
      altKeyUp = (ev: KeyboardEvent) => {
        altPressed.value = !!ev.altKey;
        if (!isOvering.value || !draggingAllocation.value) return;
        if (!altPressed.value) {
          isDuplicatingAllocation.value = false;
        }
      };
      window.addEventListener("keydown", altKeyDown, {
        capture: true,
        passive: false,
      });
      window.addEventListener("keyup", altKeyUp, {
        capture: true,
        passive: false,
      });
    });
    onBeforeUnmount(() => {
      if (altKeyDown) window.removeEventListener("keydown", altKeyDown, true);
      if (altKeyUp) window.removeEventListener("keyup", altKeyUp, true);
      breakBlockEl.value = undefined;
    });
    // Break state
    const { duration, minutes, ongoing, style } = useHeightOfDuration(
      event,
      now
    );
    const isToday = computed(() => equalDates(date.value, now.value));
    const dayName = computed(() => (isToday.value ? "today" : "on that day"));

    // Break time display
    const hideMessage = computed<boolean>(
      () =>
        !!dndEvent.value &&
        (isEqual(event.value.start, dndEvent.value.end) ||
          isEqual(event.value.end, dndEvent.value.start))
    );
    const remainingTime = computed<{ duration: string; urgent: boolean }>(
      () => {
        const _now = now.value;
        _now.setSeconds(0);
        const duration = intervalToDuration({
          start: _now,
          end: event.value.end,
        });
        return {
          duration: formatDurationFull(duration),
          urgent:
            (!duration.minutes || duration.minutes <= 30) &&
            (!duration.hours || duration.hours === 0),
        };
      }
    );

    // Drag n Drop
    const dndEvent = computed(useDndEvent);
    const draggingTask = computed(() => isDraggingTask());
    const draggingAllocation = computed(() => isDraggingAllocation());
    const showCalendarInfo = computed(
      () => draggingTask.value && !isDuplicatingAllocation.value
    );
    const { computeDropTime, dropTime, dropDuration } = useDropEvent(
      event,
      placeholderPosition
    );
    const isTimeBlockingEntitled = (): boolean => {
      if (isEntitledToTimeBlocking.value) return true;
      openUpgradeModal(billing, undefined, "time-blocking");
      isOvering.value = false;
      isDuplicatingAllocation.value = false;
      lastDraggedType.value = null;
      placeholderPosition.value = undefined;
      emit("scrollTop", false);
      emit("scrollBottom", false);
      return false;
    };

    // Refuse the drop (no drop affordance, no drop event) for tasks that cannot
    // be allocated, i.e. bound tasks surfaced from a database row.
    const acceptsData = (data: { task?: { canAllocate?: boolean } }) =>
      data?.task?.canAllocate !== false;

    const onDrop = (e: DraggedTask | DraggedEvent | DraggedAllocation) => {
      emit("drop");
      if (e.success && !!dropTime.value && dropDuration.value) {
        if (e.type === "task") {
          if (!isTimeBlockingEntitled()) return;
          allocateTimeForTask(
            controller,
            profile.value.user.accountId,
            e.data.task,
            {
              calendar: selectedCalendar.value?.id,
              duration:
                dropDuration.value <= 15 ? dropDuration.value * 60 : 1800,
              dates: { type: "date", date: dropTime.value },
              daytime: [
                {
                  hours: dropTime.value.getHours(),
                  minutes: dropTime.value.getMinutes(),
                  seconds: dropTime.value.getSeconds(),
                },
                null,
              ],
            },
            "app"
          ).then((eventId) => {
            if (eventId) {
              controller.event.update(
                {
                  id: eventId,
                  attendance: "attend",
                },
                false,
                "app"
              );
            }
          });
        } else if (e.type === "allocation" && e.native.altKey) {
          if (!isTimeBlockingEntitled()) return;
          // Duplicate allocation while holding Alt/Option
          // Use the same calendar as the original allocation
          const allocationDurationMinutes = getDuration(
            e.data.event.start,
            e.data.event.end
          );
          allocateTimeForTask(
            controller,
            profile.value.user.accountId,
            e.data.task,
            {
              calendar: e.data.event.calendar,
              duration: allocationDurationMinutes * 60,
              dates: { type: "date", date: dropTime.value },
              daytime: [
                {
                  hours: dropTime.value.getHours(),
                  minutes: dropTime.value.getMinutes(),
                  seconds: dropTime.value.getSeconds(),
                },
                null,
              ],
            },
            "app"
          ).then(async (eventId) => {
            if (eventId) {
              // Preserve the original allocation's attendance/availability
              await controller.event.update(
                {
                  id: eventId,
                  attendance: e.data.event.attendance || "attend",
                  availability_override: e.data.event.availability_override,
                },
                false,
                "app"
              );
            }
          });
        } else if (e.type === "event" || e.type === "allocation") {
          const patch = {
            id: e.data.event.id,
            time: {
              start_time: dropTime.value,
              end_time: add(dropTime.value, {
                minutes:
                  (e.data.event.end.getTime() - e.data.event.start.getTime()) /
                  (1000 * 60),
              }),
            },
          };
          emit("updateEvent", patch);
        }
      }
      isOvering.value = false;
      isDuplicatingAllocation.value = false;
      lastDraggedType.value = null;
      placeholderPosition.value = undefined;
      emit("scrollTop", false);
      emit("scrollBottom", false);
    };
    const onDragOver = (e: DraggedTask | DraggedEvent | DraggedAllocation) => {
      if (e.type === "task" && !e.data.task.canAllocate) return;
      emit("dragover", e);
      isOvering.value = true;
      lastDraggedType.value = e.type;

      // Sync altPressed from the native mouse event so that BreakBlocks
      // created mid-drag (e.g. when displayEvents toggles between merged
      // and unmerged) pick up the correct modifier state immediately.
      if (e.native) {
        altPressed.value = !!e.native.altKey;
      }

      // Update last dragged type using global state to avoid misclassification
      lastDraggedType.value = draggingAllocation.value
        ? "allocation"
        : draggingTask.value
          ? "task"
          : "event";
      isDuplicatingAllocation.value =
        draggingAllocation.value && altPressed.value;
      if (!e.position) return;
      const rect = breakBlockEl.value?.$el.getBoundingClientRect();
      const temporaryPosition =
        Math.floor((e.position.y - rect.top) / step) * step;
      const savedPlaceholderPosition = placeholderPosition.value;
      placeholderPosition.value = temporaryPosition;

      const duration =
        e.type === "event" || e.type === "allocation"
          ? getDuration(e.data.event.start, e.data.event.end)
          : DEFAULT_TASK_DURATION;
      const doesFit = duration <= (dropDuration.value || 0);

      if (
        !doesFit ||
        (dropTime.value &&
          (isBefore(dropTime.value, event.value.start) ||
            isAfter(dropTime.value, event.value.end)))
      ) {
        placeholderPosition.value = savedPlaceholderPosition;
      }

      const windowRect = document.body.getBoundingClientRect();
      emit("scrollBottom", e.position.y > windowRect.bottom - 150);
      emit("scrollTop", e.position.y < windowRect.top + 150);
    };
    const onDragLeave = () => {
      emit("dragleave");
      isOvering.value = false;
      isDuplicatingAllocation.value = false;
      lastDraggedType.value = null;
      placeholderPosition.value = undefined;
      emit("scrollTop", false);
      emit("scrollBottom", false);
    };
    const eventOrPause = computed<EventOrPause>(() => ({
      ...event.value,
      type: "pause",
    }));
    watch(isEmpty, (next) => {
      if (next) {
        isOvering.value = false;
      }
    });
    watch(isOvering, (value) => {
      if (isEmpty.value) emit("isOvering", value);
    });

    const resetDrag = () => {
      isOvering.value = false;
      placeholderPosition.value = undefined;
      emit("scrollTop", false);
      emit("scrollBottom", false);
    };

    watch(
      () => props.resetDragSignal,
      () => {
        resetDrag();
      }
    );
    return {
      breakBlockEl,
      emptyStateImage,
      resetDrag,
      acceptsData,
      computeDropTime,
      dayName,
      dropDuration,
      dropTime,
      duration,
      eventOrPause,
      formatDuration,
      hideMessage,
      isOvering,
      isDuplicatingAllocation,
      showCalendarInfo,
      lastDraggedType,
      isToday,
      locale,
      minutes,
      onDragLeave,
      onDragOver,
      onDrop,
      ongoing,
      placeholderPosition,
      dndEvent,
      draggingTask,
      draggingAllocation,
      remainingTime,
      selectedCalendar,
      style,
    };
  },
});
</script>

<style lang="scss" scoped>
.break-block {
  position: relative;
  flex-grow: 1;
  min-height: 24px;
  @include themed() {
    color: t($grey-40);
  }
  .empty {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100%;
    .cache {
      position: absolute;
      height: 100%;
      width: 15%;
      @include themed() {
        background: t($cache);
      }
    }
    .empty-state {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      row-gap: 16px;
      overflow: hidden;
    }
    .empty-state__illu {
      width: 100%;
      max-width: 468px;
      height: auto;
      pointer-events: none;
      user-select: none;
    }
    .empty-state__text {
      position: absolute;
      top: 26.5%;
      inset-inline-start: 50%;
      transform: translate(-50%, -50%);
      @extend %font-caption-regular;
      @include themed() {
        color: t($grey-60);
      }
    }
  }
  .break {
    height: 100%;
    display: flex;
    flex-flow: row;
    position: relative;

    .break-space {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
    }
    &.empty-dnd {
      flex-direction: column;

      .timer {
        height: 100%;
        position: relative;
      }
    }
    .message {
      display: flex;
      flex-direction: column;
      row-gap: 8px;
      @extend %font-caption-regular;
      text-align: center;
      margin-top: 0px;
      margin-bottom: 0px;
      @include themed() {
        color: t($grey-40);
      }

      &.evening {
        top: calc(50% + 20px);
      }
      .motivation {
        padding: 8px 16px;
        border-radius: 30px;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        @include themed() {
          color: t($grey-60);
          background: t($bg-hover);
        }
      }
      .duration.current {
        @include themed() {
          color: t($grey-60);
        }
        &.urgent {
          @include themed() {
            color: t($secondary);
          }
        }
      }
    }
  }
}
</style>
